import {
  validRegistrationCollections,
  type RegistrationCollections,
} from "@tu/conference-connector-contract";
export type ConferenceLifecycle = "active" | "upcoming" | "ended";
export type ConferenceFreshness = "never_synced" | "fresh" | "stale";
export type AnalyticsCoverage = "complete" | "partial" | "unavailable";
export interface ConferenceDisplayNames {
  readonly ar: string;
  readonly en: string;
}
export function validDisplayNames(
  value: unknown,
): value is ConferenceDisplayNames {
  if (
    !isRecord(value) ||
    Object.keys(value).some((key) => key !== "ar" && key !== "en")
  )
    return false;
  return [value.ar, value.en].every(
    (name) =>
      typeof name === "string" &&
      name.trim().length > 0 &&
      name.length <= 160 &&
      !/[<>\p{Cc}\u202A-\u202E\u2066-\u2069]/u.test(name),
  );
}

export function validConferenceWebsiteUrl(value: unknown): value is string {
  if (typeof value !== "string" || value.length === 0 || value.length > 2048)
    return false;
  let decoded = value;
  try {
    for (let depth = 0; depth < 8; depth += 1) {
      if (/[\\\p{Cc}\p{Cf}]/u.test(decoded)) return false;
      const next = decodeURIComponent(decoded);
      if (next === decoded) break;
      if (depth === 7) return false;
      decoded = next;
    }
    const url = new URL(value);
    const hostname = url.hostname.replace(/\.$/, "");
    return (
      url.protocol === "https:" &&
      !url.port &&
      url.href.length <= 2048 &&
      !url.username &&
      !url.password &&
      hostname.includes(".") &&
      !hostname.includes(":") &&
      !/^\d+(?:\.\d+){3}$/.test(hostname) &&
      !/(?:^|\.)(?:localhost|localdomain|local|internal|lan|home)$/i.test(
        hostname,
      )
    );
  } catch {
    return false;
  }
}
export type AnalyticsLifetime =
  | {
      readonly availability: "available";
      readonly totalUsers: number;
      readonly startDate: string;
      readonly endDate: string;
    }
  | {
      readonly availability: "unavailable";
      readonly totalUsers: null;
      readonly startDate: null;
      readonly endDate: null;
    };

export function validAnalyticsLifetime(
  value: unknown,
): value is AnalyticsLifetime {
  if (
    !isRecord(value) ||
    Object.keys(value).length !== 4 ||
    !["availability", "totalUsers", "startDate", "endDate"].every((key) =>
      Object.hasOwn(value, key),
    )
  )
    return false;
  if (value.availability === "unavailable")
    return (
      value.totalUsers === null &&
      value.startDate === null &&
      value.endDate === null
    );
  const validDate = (date: unknown): date is string => {
    if (typeof date !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(date))
      return false;
    const time = Date.parse(`${date}T00:00:00Z`);
    return (
      Number.isFinite(time) &&
      new Date(time).toISOString().slice(0, 10) === date
    );
  };
  return (
    value.availability === "available" &&
    isNonnegativeInteger(value.totalUsers) &&
    validDate(value.startDate) &&
    validDate(value.endDate) &&
    value.startDate <= value.endDate
  );
}
export type ConferenceBrandingAssetKind = "icon" | "logo";
export type ConferenceBrandingContentType =
  "image/png" | "image/jpeg" | "image/webp" | "image/svg+xml";

export interface ConferenceBrandingAsset {
  readonly version: string;
  readonly storagePath: string;
  readonly url: string;
  readonly contentType: ConferenceBrandingContentType;
  readonly width: number;
  readonly height: number;
  readonly byteSize: number;
  readonly updatedAt: string;
}

export interface ConferenceBrandingOverride {
  readonly source: "admin_upload";
  readonly icon: ConferenceBrandingAsset | null;
  readonly logo: ConferenceBrandingAsset | null;
  readonly updatedAt: string;
}

export interface PortfolioConferenceSummary {
  readonly displayNames?: ConferenceDisplayNames | null;
  readonly conferenceId: string;
  readonly name: string;
  readonly lifecycle: ConferenceLifecycle;
  readonly startsAt: string;
  readonly endsAt: string;
  readonly iconUrl?: string | null;
  readonly logoUrl: string | null;
  readonly registrationsTotal: number | null;
  readonly netCollectedCents: number | null;
  readonly visitorsThisMonth: number | null;
  readonly freshness: ConferenceFreshness;
  readonly connectionStatus:
    "connected" | "degraded" | "never_synced" | "upgrade_required" | "disabled";
  readonly lastSuccessfulSyncAt: string | null;
}

export interface PortfolioVisitorPoint {
  readonly historyDate: string;
  readonly visitorsThisMonth: number;
}

export interface PortfolioOverview {
  readonly generatedAt: string;
  readonly sourceSynchronizedAt: string | null;
  readonly currency: "SAR";
  readonly metrics: {
    readonly totalConferences: number;
    readonly activeConferences: number;
    readonly totalRegistrations: number;
    readonly netCollectedCents: number;
    readonly outstandingInvoicesCents: number;
    readonly sponsorshipRevenueCents: number;
    readonly otherRevenueCents: number;
    readonly visitorsThisMonth: number | null;
  };
  readonly analyticsCoverage: AnalyticsCoverage;
  readonly freshness: "fresh" | "partially_stale" | "empty";
  readonly conferences: readonly PortfolioConferenceSummary[];
  readonly visitorTrend: readonly PortfolioVisitorPoint[];
}

export interface PortfolioOverviewClient {
  load(signal?: AbortSignal): Promise<PortfolioOverview>;
}

export interface ConferenceDirectoryClient extends PortfolioOverviewClient {
  disableConference(conferenceId: string): Promise<void>;
}

export interface ConferenceDiscoveryPreview {
  readonly projectId: string;
  readonly conferenceKey: string;
  readonly name: string;
  readonly timezone: string;
  readonly startsAt: string;
  readonly endsAt: string;
  readonly status: "planned" | "active" | "ended" | "archived";
  readonly objective: string | null;
  readonly logoUrl: string | null;
  readonly connectorRegion: string;
  readonly connectorUrl: string;
  readonly componentVersions: Readonly<{
    connector: string;
    adapter: string;
    source: string;
  }>;
  readonly capabilities: Readonly<{
    registrationMetrics: Readonly<{
      supported: boolean;
      mode: "automated" | "unavailable";
    }>;
    paymentMetrics: Readonly<{
      supported: boolean;
      mode: "automated" | "unavailable";
    }>;
    ga4Metrics: Readonly<{
      supported: boolean;
      mode: "automated" | "unavailable";
    }>;
  }>;
}

export interface ConferenceDetail {
  readonly displayNames?: ConferenceDisplayNames | null;
  readonly conferenceId: string;
  readonly projectId: string;
  readonly name: string;
  readonly lifecycle: ConferenceLifecycle;
  readonly startsAt: string;
  readonly endsAt: string;
  readonly objective: string | null;
  readonly iconUrl: string | null;
  readonly logoUrl: string | null;
  readonly brandingOverride: ConferenceBrandingOverride | null;
  readonly effectiveIconUrl: string | null;
  readonly effectiveLogoUrl: string | null;
  readonly websiteUrl: string;
  readonly websiteUrlOverride?: string | null;
  readonly freshness: ConferenceFreshness;
  readonly connectionStatus: PortfolioConferenceSummary["connectionStatus"];
  readonly lastSuccessfulSyncAt: string | null;
  readonly registrations: {
    readonly total: number | null;
    readonly completed: number | null;
    readonly pending: number | null;
    readonly cancelled: number | null;
  };
  readonly finance: {
    readonly registrationCollections?: RegistrationCollections | null;
    readonly currency: "SAR";
    readonly registrationCollectedCents: number | null;
    readonly invoicesIssuedCents: number | null;
    readonly invoicesOutstandingCents: number | null;
    readonly collectedInvoiceCount: number | null;
    readonly outstandingInvoiceCount: number | null;
    readonly sponsorshipRevenueCents: number;
    readonly otherRevenueCents: number;
    readonly netCollectedCents: number | null;
  };
  readonly trends: readonly {
    readonly historyDate: string;
    readonly registrationsTotal: number;
    readonly collectedAmountCents: number;
    readonly pendingInvoiceAmountCents: number;
  }[];
  readonly analytics: {
    readonly activeUsersToday: number | null;
    readonly activeUsersYesterday: number | null;
    readonly activeUsersThisMonth: number | null;
    readonly activeUsersLast30Minutes: number | null;
    readonly lifetime?: AnalyticsLifetime | null;
    readonly countries: readonly {
      readonly countryCode: string;
      readonly visitors: number;
    }[];
    readonly dailyVisitors: readonly {
      readonly date: string;
      readonly visitors: number;
    }[];
  };
  readonly dataStatus: {
    readonly registration: "available" | "unavailable";
    readonly payment: "available" | "unavailable";
    readonly analytics: "available" | "stale" | "unavailable";
    readonly sourceUpdatedAt: string | null;
  };
}

export interface SponsorshipAgreement {
  readonly sponsorshipId: string;
  readonly conferenceId: string;
  readonly sponsorOrganization: string;
  readonly packageName: string;
  readonly currency: string;
  readonly contractedAmountMinor: number;
  readonly invoicedAmountMinor: number;
  readonly collectedAmountMinor: number;
  readonly uninvoicedAmountMinor: number;
  readonly invoiceOutstandingAmountMinor: number;
  readonly outstandingAmountMinor: number;
  readonly agreementReference: string;
  readonly evidenceAttachmentId: string;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface CreateSponsorshipInput {
  readonly conferenceId: string;
  readonly sponsorOrganization: string;
  readonly packageName: string;
  readonly currency: string;
  readonly contractedAmountMinor: number;
  readonly agreementReference: string;
  readonly evidenceAttachmentId: string;
}

export interface RecordSponsorshipInvoiceInput {
  readonly amountMinor: number;
  readonly currency: string;
  readonly reference: string;
  readonly evidenceAttachmentId: string;
  readonly issuedAt: string;
}

export interface RecordSponsorshipPaymentInput {
  readonly amountMinor: number;
  readonly currency: string;
  readonly method: "bank_transfer" | "bank_deposit" | "other";
  readonly reference: string;
  readonly evidenceAttachmentId: string;
  readonly receivedAt: string;
}

export type OtherRevenueCategory =
  "exhibition" | "workshop" | "grant" | "donation" | "service" | "other";

export interface OtherRevenueRecord {
  readonly revision: number;
  readonly otherRevenueId: string;
  readonly conferenceId: string;
  readonly sourceOrganization: string;
  readonly category: OtherRevenueCategory;
  readonly description: string;
  readonly amountMinor: number;
  readonly currency: string;
  readonly method: "bank_transfer" | "bank_deposit" | "other";
  readonly reference: string;
  readonly evidenceAttachmentId: string;
  readonly receivedAt: string;
  readonly status: "active" | "reversed";
  readonly reversedAt: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface CreateOtherRevenueInput {
  readonly conferenceId: string;
  readonly sourceOrganization: string;
  readonly category: OtherRevenueCategory;
  readonly description: string;
  readonly amountMinor: number;
  readonly currency: string;
  readonly method: "bank_transfer" | "bank_deposit" | "other";
  readonly reference: string;
  readonly evidenceAttachmentId: string;
  readonly receivedAt: string;
}

export type UpdateOtherRevenueInput = Omit<
  CreateOtherRevenueInput,
  "conferenceId" | "currency"
> & { readonly expectedRevision: number };

export interface ExpenseRecord {
  readonly expenseId: string;
  readonly conferenceId: string;
  readonly title: string;
  readonly category:
    | "operations"
    | "marketing"
    | "hospitality"
    | "technology"
    | "speakers"
    | "travel_accommodation"
    | "equipment"
    | "other";
  readonly amountMinor: number;
  readonly currency: string;
  readonly incurredAt: string;
  readonly status: "active" | "reversed";
  readonly reversedAt: string | null;
  readonly revision: number;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface CreateExpenseInput {
  readonly idempotencyKey: string;
  readonly conferenceId: string;
  readonly title: string;
  readonly category: ExpenseRecord["category"];
  readonly amountMinor: number;
  readonly currency: string;
  readonly incurredAt: string;
}

export interface UpdateExpenseInput {
  readonly title: string;
  readonly category: ExpenseRecord["category"];
  readonly amountMinor: number;
  readonly incurredAt: string;
  readonly expectedRevision: number;
}

export interface ConferenceDashboardClient extends ConferenceDirectoryClient {
  updateWebsite?(
    conferenceId: string,
    websiteUrl: string | null,
  ): Promise<string | null>;
  updateConferenceNames?(
    conferenceId: string,
    names: ConferenceDisplayNames,
  ): Promise<ConferenceDisplayNames>;
  discoverConference(
    projectId: string,
    signal?: AbortSignal,
  ): Promise<ConferenceDiscoveryPreview>;
  activateConferenceRegistration(
    projectId: string,
  ): Promise<Readonly<{ conferenceId: string; status: "active" }>>;
  loadConferenceDetail(
    conferenceId: string,
    signal?: AbortSignal,
  ): Promise<ConferenceDetail>;
  uploadConferenceBranding(
    conferenceId: string,
    kind: ConferenceBrandingAssetKind,
    image: Blob,
  ): Promise<ConferenceBrandingOverride>;
  removeConferenceBranding(
    conferenceId: string,
    kind: ConferenceBrandingAssetKind,
  ): Promise<ConferenceBrandingOverride | null>;
  loadSponsorships(
    conferenceId: string,
    signal?: AbortSignal,
  ): Promise<readonly SponsorshipAgreement[]>;
  createSponsorship(
    input: CreateSponsorshipInput,
  ): Promise<SponsorshipAgreement>;
  recordSponsorshipInvoice(
    sponsorshipId: string,
    input: RecordSponsorshipInvoiceInput,
  ): Promise<SponsorshipAgreement>;
  recordSponsorshipPayment(
    sponsorshipId: string,
    input: RecordSponsorshipPaymentInput,
  ): Promise<SponsorshipAgreement>;
  loadOtherRevenue(
    conferenceId: string,
    signal?: AbortSignal,
  ): Promise<readonly OtherRevenueRecord[]>;
  createOtherRevenue(
    input: CreateOtherRevenueInput,
  ): Promise<OtherRevenueRecord>;
  reverseOtherRevenue(otherRevenueId: string): Promise<OtherRevenueRecord>;
  updateOtherRevenue(
    otherRevenueId: string,
    input: UpdateOtherRevenueInput,
  ): Promise<OtherRevenueRecord>;
  loadExpenses(
    conferenceId: string,
    signal?: AbortSignal,
  ): Promise<readonly ExpenseRecord[]>;
  createExpense(input: CreateExpenseInput): Promise<ExpenseRecord>;
  updateExpense(
    expenseId: string,
    input: UpdateExpenseInput,
  ): Promise<ExpenseRecord>;
  reverseExpense(expenseId: string): Promise<ExpenseRecord>;
}

type TokenProvider = (forceRefresh?: boolean) => Promise<string>;
type Fetch = typeof fetch;
const browserFetch: Fetch = (input, init) => globalThis.fetch(input, init);

export const PORTFOLIO_ACCESS_ERROR_CODES = [
  "account_inactive",
  "mfa_enrollment_required",
  "mfa_challenge_required",
  "provider_not_allowed",
  "profile_not_found",
  "unverified_email",
  "permission_denied",
] as const;

export type PortfolioAccessErrorCode =
  (typeof PORTFOLIO_ACCESS_ERROR_CODES)[number];

export function isPortfolioAccessErrorCode(
  value: unknown,
): value is PortfolioAccessErrorCode {
  return PORTFOLIO_ACCESS_ERROR_CODES.some((code) => code === value);
}

export type PortfolioApiErrorCode =
  | "session_expired"
  | "request_failed"
  | "invalid_response"
  | PortfolioAccessErrorCode;

export class PortfolioApiError extends Error {
  readonly code: PortfolioApiErrorCode;

  constructor(code: PortfolioApiErrorCode) {
    super(code);
    this.name = "PortfolioApiError";
    this.code = code;
  }
}

export type ConferenceBrandingApiErrorCode =
  | "conference_not_found"
  | "file_too_large"
  | "invalid_image"
  | "invalid_conference_website"
  | "storage_unavailable"
  | "request_failed";

export class ConferenceBrandingApiError extends Error {
  readonly code: ConferenceBrandingApiErrorCode;

  constructor(code: ConferenceBrandingApiErrorCode) {
    super(code);
    this.name = "ConferenceBrandingApiError";
    this.code = code;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isNonnegativeInteger(value: unknown): value is number {
  return Number.isSafeInteger(value) && Number(value) >= 0;
}

function validConference(value: unknown): value is PortfolioConferenceSummary {
  if (!isRecord(value)) return false;
  if (value.displayNames != null && !validDisplayNames(value.displayNames))
    return false;
  return (
    typeof value.conferenceId === "string" &&
    typeof value.name === "string" &&
    ["active", "upcoming", "ended"].includes(String(value.lifecycle)) &&
    typeof value.startsAt === "string" &&
    typeof value.endsAt === "string" &&
    (value.iconUrl === undefined ||
      value.iconUrl === null ||
      typeof value.iconUrl === "string") &&
    (value.logoUrl === null || typeof value.logoUrl === "string") &&
    (value.registrationsTotal === null ||
      isNonnegativeInteger(value.registrationsTotal)) &&
    (value.netCollectedCents === null ||
      isNonnegativeInteger(value.netCollectedCents)) &&
    (value.visitorsThisMonth === null ||
      isNonnegativeInteger(value.visitorsThisMonth)) &&
    ["never_synced", "fresh", "stale"].includes(String(value.freshness)) &&
    [
      "connected",
      "degraded",
      "never_synced",
      "upgrade_required",
      "disabled",
    ].includes(String(value.connectionStatus)) &&
    (value.lastSuccessfulSyncAt === null ||
      typeof value.lastSuccessfulSyncAt === "string")
  );
}

export function parsePortfolioOverview(value: unknown): PortfolioOverview {
  if (!isRecord(value) || !isRecord(value.metrics)) {
    throw new PortfolioApiError("invalid_response");
  }
  const metrics = value.metrics;
  const moneyFields = [
    metrics.netCollectedCents,
    metrics.outstandingInvoicesCents,
    metrics.sponsorshipRevenueCents,
    metrics.otherRevenueCents,
  ];
  const countFields = [
    metrics.totalConferences,
    metrics.activeConferences,
    metrics.totalRegistrations,
  ];
  if (
    typeof value.generatedAt !== "string" ||
    (value.sourceSynchronizedAt !== null &&
      typeof value.sourceSynchronizedAt !== "string") ||
    value.currency !== "SAR" ||
    !countFields.every(isNonnegativeInteger) ||
    !moneyFields.every(isNonnegativeInteger) ||
    (metrics.visitorsThisMonth !== null &&
      !isNonnegativeInteger(metrics.visitorsThisMonth)) ||
    !["complete", "partial", "unavailable"].includes(
      String(value.analyticsCoverage),
    ) ||
    !["fresh", "partially_stale", "empty"].includes(String(value.freshness)) ||
    !Array.isArray(value.conferences) ||
    !value.conferences.every(validConference) ||
    !Array.isArray(value.visitorTrend) ||
    !value.visitorTrend.every(
      (point) =>
        isRecord(point) &&
        typeof point.historyDate === "string" &&
        isNonnegativeInteger(point.visitorsThisMonth),
    )
  ) {
    throw new PortfolioApiError("invalid_response");
  }
  return value as unknown as PortfolioOverview;
}

function nullableNonnegativeInteger(value: unknown): value is number | null {
  return value === null || isNonnegativeInteger(value);
}

function validConferenceFinance(value: Record<string, unknown>): boolean {
  return (
    value.currency === "SAR" &&
    (value.registrationCollections == null ||
      validRegistrationCollections(value.registrationCollections)) &&
    [
      value.registrationCollectedCents,
      value.invoicesIssuedCents,
      value.invoicesOutstandingCents,
      value.collectedInvoiceCount,
      value.outstandingInvoiceCount,
      value.netCollectedCents,
    ].every(nullableNonnegativeInteger) &&
    [value.sponsorshipRevenueCents, value.otherRevenueCents].every(
      isNonnegativeInteger,
    )
  );
}

function validConferenceBrandingAsset(
  value: unknown,
): value is ConferenceBrandingAsset {
  if (!isRecord(value)) return false;
  return (
    typeof value.version === "string" &&
    value.version.length > 0 &&
    typeof value.storagePath === "string" &&
    value.storagePath.length > 0 &&
    typeof value.url === "string" &&
    value.url.length > 0 &&
    ["image/png", "image/jpeg", "image/webp", "image/svg+xml"].includes(
      String(value.contentType),
    ) &&
    isNonnegativeInteger(value.width) &&
    value.width > 0 &&
    isNonnegativeInteger(value.height) &&
    value.height > 0 &&
    isNonnegativeInteger(value.byteSize) &&
    value.byteSize > 0 &&
    typeof value.updatedAt === "string"
  );
}

export function parseConferenceBrandingOverride(
  value: unknown,
): ConferenceBrandingOverride {
  if (
    !isRecord(value) ||
    value.source !== "admin_upload" ||
    (value.icon !== null && !validConferenceBrandingAsset(value.icon)) ||
    (value.logo !== null && !validConferenceBrandingAsset(value.logo)) ||
    typeof value.updatedAt !== "string"
  ) {
    throw new PortfolioApiError("invalid_response");
  }
  return value as unknown as ConferenceBrandingOverride;
}

function validOptionalConferenceBrandingOverride(value: unknown): boolean {
  if (value === undefined || value === null) return true;
  try {
    parseConferenceBrandingOverride(value);
    return true;
  } catch {
    return false;
  }
}

export function parseConferenceDetail(value: unknown): ConferenceDetail {
  if (
    !isRecord(value) ||
    !isRecord(value.registrations) ||
    !isRecord(value.finance) ||
    !isRecord(value.analytics) ||
    !isRecord(value.dataStatus) ||
    typeof value.conferenceId !== "string" ||
    typeof value.projectId !== "string" ||
    typeof value.name !== "string" ||
    (value.displayNames != null && !validDisplayNames(value.displayNames)) ||
    !["active", "upcoming", "ended"].includes(String(value.lifecycle)) ||
    typeof value.startsAt !== "string" ||
    typeof value.endsAt !== "string" ||
    (value.objective !== null && typeof value.objective !== "string") ||
    (value.iconUrl !== undefined &&
      value.iconUrl !== null &&
      typeof value.iconUrl !== "string") ||
    (value.logoUrl !== null && typeof value.logoUrl !== "string") ||
    !validOptionalConferenceBrandingOverride(value.brandingOverride) ||
    (value.effectiveIconUrl !== undefined &&
      value.effectiveIconUrl !== null &&
      typeof value.effectiveIconUrl !== "string") ||
    (value.effectiveLogoUrl !== undefined &&
      value.effectiveLogoUrl !== null &&
      typeof value.effectiveLogoUrl !== "string") ||
    !validConferenceWebsiteUrl(value.websiteUrl) ||
    (value.websiteUrlOverride != null &&
      !validConferenceWebsiteUrl(value.websiteUrlOverride)) ||
    !["never_synced", "fresh", "stale"].includes(String(value.freshness)) ||
    ![
      "connected",
      "degraded",
      "never_synced",
      "upgrade_required",
      "disabled",
    ].includes(String(value.connectionStatus)) ||
    (value.lastSuccessfulSyncAt !== null &&
      typeof value.lastSuccessfulSyncAt !== "string") ||
    !Object.values(value.registrations).every(nullableNonnegativeInteger) ||
    !validConferenceFinance(value.finance) ||
    !nullableNonnegativeInteger(value.analytics.activeUsersToday) ||
    !nullableNonnegativeInteger(value.analytics.activeUsersYesterday) ||
    !nullableNonnegativeInteger(value.analytics.activeUsersThisMonth) ||
    !nullableNonnegativeInteger(value.analytics.activeUsersLast30Minutes) ||
    (value.analytics.lifetime !== undefined &&
      value.analytics.lifetime !== null &&
      !validAnalyticsLifetime(value.analytics.lifetime)) ||
    !Array.isArray(value.analytics.countries) ||
    !value.analytics.countries.every(
      (country) =>
        isRecord(country) &&
        typeof country.countryCode === "string" &&
        /^[A-Z]{2}$/.test(country.countryCode) &&
        isNonnegativeInteger(country.visitors),
    ) ||
    !Array.isArray(value.analytics.dailyVisitors) ||
    !value.analytics.dailyVisitors.every(
      (point) =>
        isRecord(point) &&
        typeof point.date === "string" &&
        /^\d{4}-\d{2}-\d{2}$/.test(point.date) &&
        isNonnegativeInteger(point.visitors),
    ) ||
    !Array.isArray(value.trends) ||
    !value.trends.every(
      (point) =>
        isRecord(point) &&
        typeof point.historyDate === "string" &&
        isNonnegativeInteger(point.registrationsTotal) &&
        isNonnegativeInteger(point.collectedAmountCents) &&
        isNonnegativeInteger(point.pendingInvoiceAmountCents),
    ) ||
    !["available", "unavailable"].includes(
      String(value.dataStatus.registration),
    ) ||
    !["available", "unavailable"].includes(String(value.dataStatus.payment)) ||
    !["available", "stale", "unavailable"].includes(
      String(value.dataStatus.analytics),
    ) ||
    (value.dataStatus.sourceUpdatedAt !== null &&
      typeof value.dataStatus.sourceUpdatedAt !== "string")
  ) {
    throw new PortfolioApiError("invalid_response");
  }
  const iconUrl = typeof value.iconUrl === "string" ? value.iconUrl : null;
  const logoUrl = typeof value.logoUrl === "string" ? value.logoUrl : null;
  const brandingOverride =
    value.brandingOverride === undefined || value.brandingOverride === null
      ? null
      : parseConferenceBrandingOverride(value.brandingOverride);
  return {
    ...(value as unknown as ConferenceDetail),
    iconUrl,
    logoUrl,
    brandingOverride,
    effectiveIconUrl:
      typeof value.effectiveIconUrl === "string"
        ? value.effectiveIconUrl
        : (brandingOverride?.icon?.url ?? iconUrl ?? logoUrl),
    effectiveLogoUrl:
      typeof value.effectiveLogoUrl === "string"
        ? value.effectiveLogoUrl
        : (brandingOverride?.logo?.url ?? logoUrl),
  };
}

function validSponsorship(value: unknown): value is SponsorshipAgreement {
  if (!isRecord(value)) return false;
  const amounts = [
    value.contractedAmountMinor,
    value.invoicedAmountMinor,
    value.collectedAmountMinor,
    value.uninvoicedAmountMinor,
    value.invoiceOutstandingAmountMinor,
    value.outstandingAmountMinor,
  ];
  if (
    typeof value.sponsorshipId !== "string" ||
    typeof value.conferenceId !== "string" ||
    typeof value.sponsorOrganization !== "string" ||
    typeof value.packageName !== "string" ||
    typeof value.currency !== "string" ||
    !/^[A-Z]{3}$/.test(value.currency) ||
    !amounts.every(isNonnegativeInteger) ||
    Number(value.contractedAmountMinor) <= 0 ||
    typeof value.agreementReference !== "string" ||
    typeof value.evidenceAttachmentId !== "string" ||
    typeof value.createdAt !== "string" ||
    typeof value.updatedAt !== "string"
  ) {
    return false;
  }
  const contracted = Number(value.contractedAmountMinor);
  const invoiced = Number(value.invoicedAmountMinor);
  const collected = Number(value.collectedAmountMinor);
  return (
    collected <= invoiced &&
    invoiced <= contracted &&
    value.uninvoicedAmountMinor === contracted - invoiced &&
    value.invoiceOutstandingAmountMinor === invoiced - collected &&
    value.outstandingAmountMinor === contracted - collected
  );
}

export function parseSponsorships(value: unknown) {
  if (!Array.isArray(value) || !value.every(validSponsorship)) {
    throw new PortfolioApiError("invalid_response");
  }
  return value as readonly SponsorshipAgreement[];
}

const OTHER_REVENUE_CATEGORY_VALUES = [
  "exhibition",
  "workshop",
  "grant",
  "donation",
  "service",
  "other",
] as const;

function validOtherRevenue(value: unknown): value is OtherRevenueRecord {
  if (!isRecord(value)) return false;
  return (
    isNonnegativeInteger(value.revision) &&
    typeof value.otherRevenueId === "string" &&
    typeof value.conferenceId === "string" &&
    typeof value.sourceOrganization === "string" &&
    OTHER_REVENUE_CATEGORY_VALUES.includes(value.category as never) &&
    typeof value.description === "string" &&
    isNonnegativeInteger(value.amountMinor) &&
    Number(value.amountMinor) > 0 &&
    typeof value.currency === "string" &&
    /^[A-Z]{3}$/.test(value.currency) &&
    ["bank_transfer", "bank_deposit", "other"].includes(String(value.method)) &&
    typeof value.reference === "string" &&
    typeof value.evidenceAttachmentId === "string" &&
    typeof value.receivedAt === "string" &&
    ["active", "reversed"].includes(String(value.status)) &&
    (value.reversedAt === null || typeof value.reversedAt === "string") &&
    (value.status === "active"
      ? value.reversedAt === null
      : typeof value.reversedAt === "string") &&
    typeof value.createdAt === "string" &&
    typeof value.updatedAt === "string"
  );
}

export function parseOtherRevenue(value: unknown) {
  if (!Array.isArray(value) || !value.every(validOtherRevenue)) {
    throw new PortfolioApiError("invalid_response");
  }
  return value as readonly OtherRevenueRecord[];
}

function validExpense(value: unknown): value is ExpenseRecord {
  if (!isRecord(value)) return false;
  return (
    typeof value.expenseId === "string" &&
    typeof value.conferenceId === "string" &&
    typeof value.title === "string" &&
    value.title.trim().length >= 2 &&
    [
      "operations",
      "marketing",
      "hospitality",
      "technology",
      "speakers",
      "travel_accommodation",
      "equipment",
      "other",
    ].includes(String(value.category)) &&
    isNonnegativeInteger(value.amountMinor) &&
    Number(value.amountMinor) > 0 &&
    typeof value.currency === "string" &&
    /^[A-Z]{3}$/.test(value.currency) &&
    typeof value.incurredAt === "string" &&
    ["active", "reversed"].includes(String(value.status)) &&
    (value.reversedAt === null || typeof value.reversedAt === "string") &&
    (value.status === "active"
      ? value.reversedAt === null
      : typeof value.reversedAt === "string") &&
    isNonnegativeInteger(value.revision) &&
    typeof value.createdAt === "string" &&
    typeof value.updatedAt === "string"
  );
}

export function parseExpenses(value: unknown) {
  if (!Array.isArray(value) || !value.every(validExpense)) {
    throw new PortfolioApiError("invalid_response");
  }
  return value as readonly ExpenseRecord[];
}

function validCapability(value: unknown) {
  return (
    isRecord(value) &&
    typeof value.supported === "boolean" &&
    ["automated", "unavailable"].includes(String(value.mode)) &&
    value.supported === (value.mode === "automated")
  );
}

function validComponentVersions(value: unknown) {
  if (!isRecord(value)) return false;
  return ["connector", "adapter", "source"].every((component) =>
    /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)$/.test(
      String(value[component] ?? ""),
    ),
  );
}

export function parseConferenceDiscoveryPreview(
  value: unknown,
): ConferenceDiscoveryPreview {
  if (
    !isRecord(value) ||
    !isRecord(value.capabilities) ||
    !validComponentVersions(value.componentVersions) ||
    typeof value.projectId !== "string" ||
    typeof value.conferenceKey !== "string" ||
    value.projectId !== value.conferenceKey ||
    typeof value.name !== "string" ||
    typeof value.timezone !== "string" ||
    typeof value.startsAt !== "string" ||
    typeof value.endsAt !== "string" ||
    !["planned", "active", "ended", "archived"].includes(
      String(value.status),
    ) ||
    (value.objective !== null && typeof value.objective !== "string") ||
    (value.logoUrl !== null && typeof value.logoUrl !== "string") ||
    typeof value.connectorRegion !== "string" ||
    typeof value.connectorUrl !== "string" ||
    !validCapability(value.capabilities.registrationMetrics) ||
    !validCapability(value.capabilities.paymentMetrics) ||
    !validCapability(value.capabilities.ga4Metrics)
  ) {
    throw new PortfolioApiError("invalid_response");
  }
  return value as unknown as ConferenceDiscoveryPreview;
}

export class FirebasePortfolioApiClient implements ConferenceDashboardClient {
  readonly #tokenProvider: TokenProvider;
  readonly #fetch: Fetch;
  readonly #baseUrl: string;

  async updateWebsite(
    conferenceId: string,
    websiteUrl: string | null,
  ): Promise<string | null> {
    if (websiteUrl !== null && !validConferenceWebsiteUrl(websiteUrl)) {
      throw new ConferenceBrandingApiError("invalid_conference_website");
    }
    const response = await this.#authorizedFetch(
      `${this.#baseUrl}/v1/admin/conferences/${encodeURIComponent(conferenceId)}/branding/website`,
      {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ websiteUrl }),
      },
      (failedResponse) =>
        failedResponse.status === 400
          ? new ConferenceBrandingApiError("invalid_conference_website")
          : new PortfolioApiError("request_failed"),
    );
    const result: unknown = await response.json();
    if (
      !isRecord(result) ||
      (result.websiteUrl !== null &&
        !validConferenceWebsiteUrl(result.websiteUrl))
    ) {
      throw new PortfolioApiError("invalid_response");
    }
    return result.websiteUrl;
  }

  async updateConferenceNames(
    conferenceId: string,
    names: ConferenceDisplayNames,
  ): Promise<ConferenceDisplayNames> {
    const response = await this.#authorizedFetch(
      `${this.#baseUrl}/v1/admin/conferences/${encodeURIComponent(conferenceId)}/branding/names`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(names),
      },
    );
    if (!response.ok) throw new PortfolioApiError("request_failed");
    const result: unknown = await response.json();
    if (!isRecord(result) || !validDisplayNames(result.displayNames))
      throw new PortfolioApiError("invalid_response");
    return result.displayNames;
  }

  constructor(
    tokenProvider: TokenProvider,
    baseUrl = "",
    fetchImplementation: Fetch = browserFetch,
  ) {
    this.#tokenProvider = tokenProvider;
    this.#fetch = fetchImplementation;
    this.#baseUrl = baseUrl.replace(/\/$/, "");
  }

  async #authorizedFetch(
    input: string,
    init: RequestInit,
    errorFactory?: (response: Response) => Error,
  ): Promise<Response> {
    const request = async (forceRefresh: boolean) => {
      let token: string;
      try {
        token = await this.#tokenProvider(forceRefresh);
      } catch {
        throw new PortfolioApiError("session_expired");
      }
      if (!token) throw new PortfolioApiError("session_expired");

      try {
        return await this.#fetch(input, {
          ...init,
          headers: {
            ...init.headers,
            Authorization: `Bearer ${token}`,
          },
        });
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") {
          throw error;
        }
        throw new PortfolioApiError("request_failed");
      }
    };

    let response = await request(false);
    if (response.status === 401) response = await request(true);
    if (response.status === 401) {
      throw new PortfolioApiError("session_expired");
    }
    if (response.status === 403) {
      // Only documented reason codes may reach the UI; never retain server text.
      const body: unknown = await response.json().catch(() => null);
      const code =
        isRecord(body) && isPortfolioAccessErrorCode(body.error)
          ? body.error
          : "permission_denied";
      throw new PortfolioApiError(code);
    }
    if (!response.ok) {
      throw errorFactory?.(response) ?? new PortfolioApiError("request_failed");
    }
    return response;
  }

  async load(signal?: AbortSignal): Promise<PortfolioOverview> {
    const response = await this.#authorizedFetch(
      `${this.#baseUrl}/v1/portfolio/overview`,
      {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
        signal: signal ?? null,
      },
    );
    const body: unknown = await response.json();
    if (!isRecord(body) || !("overview" in body)) {
      throw new PortfolioApiError("invalid_response");
    }
    return parsePortfolioOverview(body.overview);
  }

  async disableConference(conferenceId: string): Promise<void> {
    await this.#authorizedFetch(
      `${this.#baseUrl}/v1/admin/conferences/${encodeURIComponent(conferenceId)}:disable`,
      {
        method: "POST",
        headers: {
          Accept: "application/json",
        },
      },
    );
  }

  async discoverConference(
    projectId: string,
    signal?: AbortSignal,
  ): Promise<ConferenceDiscoveryPreview> {
    const response = await this.#authorizedFetch(
      `${this.#baseUrl}/v1/admin/conferences:discover`,
      {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ projectId }),
        signal: signal ?? null,
      },
    );
    const body: unknown = await response.json();
    if (!isRecord(body) || !("preview" in body)) {
      throw new PortfolioApiError("invalid_response");
    }
    return parseConferenceDiscoveryPreview(body.preview);
  }

  async activateConferenceRegistration(
    projectId: string,
  ): Promise<Readonly<{ conferenceId: string; status: "active" }>> {
    const response = await this.#authorizedFetch(
      `${this.#baseUrl}/v1/admin/conference-registrations/${encodeURIComponent(projectId)}:activate`,
      {
        method: "POST",
        headers: {
          Accept: "application/json",
        },
      },
    );
    const body: unknown = await response.json();
    if (
      !isRecord(body) ||
      !isRecord(body.conference) ||
      body.conference.conferenceId !== projectId ||
      body.conference.status !== "active"
    ) {
      throw new PortfolioApiError("invalid_response");
    }
    return Object.freeze({ conferenceId: projectId, status: "active" });
  }

  async loadConferenceDetail(
    conferenceId: string,
    signal?: AbortSignal,
  ): Promise<ConferenceDetail> {
    const response = await this.#authorizedFetch(
      `${this.#baseUrl}/v1/conferences/${encodeURIComponent(conferenceId)}`,
      {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
        signal: signal ?? null,
      },
    );
    const body: unknown = await response.json();
    if (!isRecord(body) || !("conference" in body)) {
      throw new PortfolioApiError("invalid_response");
    }
    return parseConferenceDetail(body.conference);
  }

  async #conferenceBrandingRequest(
    conferenceId: string,
    kind: ConferenceBrandingAssetKind,
    remove: boolean,
    image?: Blob,
  ): Promise<ConferenceBrandingOverride | null> {
    const response = await this.#authorizedFetch(
      `${this.#baseUrl}/v1/admin/conferences/${encodeURIComponent(conferenceId)}/branding/${kind}${remove ? ":remove" : ""}`,
      {
        method: "POST",
        headers: {
          Accept: "application/json",
          ...(image ? { "Content-Type": image.type } : {}),
        },
        ...(image ? { body: image } : {}),
      },
      (failedResponse) => {
        if (failedResponse.status === 400) {
          return new ConferenceBrandingApiError("invalid_image");
        }
        if (failedResponse.status === 404) {
          return new ConferenceBrandingApiError("conference_not_found");
        }
        if (failedResponse.status === 413) {
          return new ConferenceBrandingApiError("file_too_large");
        }
        if (failedResponse.status >= 500) {
          return new ConferenceBrandingApiError("storage_unavailable");
        }
        return new ConferenceBrandingApiError("request_failed");
      },
    );
    const body: unknown = await response.json();
    if (!isRecord(body) || !("brandingOverride" in body)) {
      throw new PortfolioApiError("invalid_response");
    }
    if (body.brandingOverride === null) return null;
    return parseConferenceBrandingOverride(body.brandingOverride);
  }

  async uploadConferenceBranding(
    conferenceId: string,
    kind: ConferenceBrandingAssetKind,
    image: Blob,
  ): Promise<ConferenceBrandingOverride> {
    const brandingOverride = await this.#conferenceBrandingRequest(
      conferenceId,
      kind,
      false,
      image,
    );
    if (!brandingOverride) throw new PortfolioApiError("invalid_response");
    return brandingOverride;
  }

  removeConferenceBranding(
    conferenceId: string,
    kind: ConferenceBrandingAssetKind,
  ): Promise<ConferenceBrandingOverride | null> {
    return this.#conferenceBrandingRequest(conferenceId, kind, true);
  }

  async #financialRequest(
    path: string,
    method: "GET" | "POST" | "PATCH",
    input?: unknown,
    signal?: AbortSignal,
  ): Promise<Record<string, unknown>> {
    const response = await this.#authorizedFetch(`${this.#baseUrl}${path}`, {
      method,
      headers: {
        Accept: "application/json",
        ...(input ? { "Content-Type": "application/json" } : {}),
      },
      ...(input ? { body: JSON.stringify(input) } : {}),
      signal: signal ?? null,
    });
    const body: unknown = await response.json();
    if (!isRecord(body)) throw new PortfolioApiError("invalid_response");
    return body;
  }

  async loadSponsorships(
    conferenceId: string,
    signal?: AbortSignal,
  ): Promise<readonly SponsorshipAgreement[]> {
    const body = await this.#financialRequest(
      `/v1/conferences/${encodeURIComponent(conferenceId)}/sponsorships`,
      "GET",
      undefined,
      signal,
    );
    return parseSponsorships(body.sponsorships);
  }

  async createSponsorship(input: CreateSponsorshipInput) {
    const body = await this.#financialRequest(
      "/v1/admin/sponsorships",
      "POST",
      input,
    );
    if (!validSponsorship(body.sponsorship)) {
      throw new PortfolioApiError("invalid_response");
    }
    return body.sponsorship;
  }

  async recordSponsorshipInvoice(
    sponsorshipId: string,
    input: RecordSponsorshipInvoiceInput,
  ) {
    const body = await this.#financialRequest(
      `/v1/admin/sponsorships/${encodeURIComponent(sponsorshipId)}/invoices`,
      "POST",
      input,
    );
    if (!validSponsorship(body.sponsorship)) {
      throw new PortfolioApiError("invalid_response");
    }
    return body.sponsorship;
  }

  async recordSponsorshipPayment(
    sponsorshipId: string,
    input: RecordSponsorshipPaymentInput,
  ) {
    const body = await this.#financialRequest(
      `/v1/admin/sponsorships/${encodeURIComponent(sponsorshipId)}/payments`,
      "POST",
      input,
    );
    if (!validSponsorship(body.sponsorship)) {
      throw new PortfolioApiError("invalid_response");
    }
    return body.sponsorship;
  }

  async loadOtherRevenue(
    conferenceId: string,
    signal?: AbortSignal,
  ): Promise<readonly OtherRevenueRecord[]> {
    const body = await this.#financialRequest(
      `/v1/conferences/${encodeURIComponent(conferenceId)}/other-revenue`,
      "GET",
      undefined,
      signal,
    );
    return parseOtherRevenue(body.records);
  }

  async createOtherRevenue(input: CreateOtherRevenueInput) {
    const body = await this.#financialRequest(
      "/v1/admin/other-revenue",
      "POST",
      input,
    );
    if (!validOtherRevenue(body.record)) {
      throw new PortfolioApiError("invalid_response");
    }
    return body.record;
  }

  async updateOtherRevenue(
    otherRevenueId: string,
    input: UpdateOtherRevenueInput,
  ) {
    const body = await this.#financialRequest(
      `/v1/admin/other-revenue/${encodeURIComponent(otherRevenueId)}`,
      "PATCH",
      input,
    );
    if (!validOtherRevenue(body.record))
      throw new PortfolioApiError("invalid_response");
    return body.record;
  }

  async reverseOtherRevenue(otherRevenueId: string) {
    const body = await this.#financialRequest(
      `/v1/admin/other-revenue/${encodeURIComponent(otherRevenueId)}:reverse`,
      "POST",
    );
    if (!validOtherRevenue(body.record)) {
      throw new PortfolioApiError("invalid_response");
    }
    return body.record;
  }

  async loadExpenses(
    conferenceId: string,
    signal?: AbortSignal,
  ): Promise<readonly ExpenseRecord[]> {
    const body = await this.#financialRequest(
      `/v1/conferences/${encodeURIComponent(conferenceId)}/expenses`,
      "GET",
      undefined,
      signal,
    );
    return parseExpenses(body.records);
  }

  async createExpense(input: CreateExpenseInput) {
    const body = await this.#financialRequest(
      "/v1/admin/expenses",
      "POST",
      input,
    );
    if (!validExpense(body.record)) {
      throw new PortfolioApiError("invalid_response");
    }
    return body.record;
  }

  async updateExpense(expenseId: string, input: UpdateExpenseInput) {
    const body = await this.#financialRequest(
      `/v1/admin/expenses/${encodeURIComponent(expenseId)}`,
      "PATCH",
      input,
    );
    if (!validExpense(body.record)) {
      throw new PortfolioApiError("invalid_response");
    }
    return body.record;
  }

  async reverseExpense(expenseId: string) {
    const body = await this.#financialRequest(
      `/v1/admin/expenses/${encodeURIComponent(expenseId)}:reverse`,
      "POST",
    );
    if (!validExpense(body.record)) {
      throw new PortfolioApiError("invalid_response");
    }
    return body.record;
  }
}
